// Legal Contracts service — simple object literal pattern matching
// budgetService.js. Phase 2 shipped the read-only surface; Phase 4
// added the file upload/download/delete + AI analysis poll/retry
// endpoints. Phase 6 will land the contract PATCH endpoint.

import axiosInstance from '../utils/axiosConfig';

const BASE_URL = '/api/legal';

export const legalContractService = {
  // List contracts with optional filters. The backend viewset accepts:
  //   commodity_type, is_active, is_internal, entity, source_fuel_enquiry,
  //   commence_date__gte / __lte, q (free-text), ordering, page.
  // All filters are passed through verbatim — callers are responsible for
  // sending the canonical filter names.
  list: async (params = {}) => {
    const response = await axiosInstance.get(`${BASE_URL}/contracts/`, { params });
    return response.data;
  },

  // Contracts for ONE host record — the read surface that replaced the Legal
  // module (Craig, 2026-08-26). Pass exactly one of enquiryId / deliveryId /
  // partnerId; the backend 400s otherwise rather than returning the caller's
  // whole contract book. Rows are already scoped server-side, so the caller
  // renders whatever comes back.
  inContext: async ({ enquiryId, deliveryId, partnerId, freightId } = {}) => {
    const params = {};
    if (enquiryId) params.enquiry = enquiryId;
    if (deliveryId) params.delivery = deliveryId;
    if (partnerId) params.partner = partnerId;
    if (freightId) params.freight = freightId;
    const response = await axiosInstance.get(`${BASE_URL}/contracts/in-context/`, { params });
    return response.data;
  },

  get: async (id) => {
    const response = await axiosInstance.get(`${BASE_URL}/contracts/${id}/`);
    return response.data;
  },

  // --- File lifecycle (Phase 4) -------------------------------------------

  // Upload a single file. ``onUploadProgress`` is an axios-style
  // callback receiving ``{ loaded, total }``. Returns the new file row
  // (with nested ``analysis`` in 'pending' state) so the caller can
  // render the file immediately without a re-fetch.
  uploadFile: async (contractId, file, onUploadProgress) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post(
      `${BASE_URL}/contracts/${contractId}/files/`,
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress,
      },
    );
    return response.data;
  },

  deleteFile: async (contractId, fileId) => {
    return axiosInstance.delete(
      `${BASE_URL}/contracts/${contractId}/files/${fileId}/`,
    );
  },

  // Authenticated streaming download. Returns a Blob ready for
  // ``URL.createObjectURL`` so the caller can trigger a browser save.
  downloadFile: async (contractId, fileId) => {
    const response = await axiosInstance.get(
      `${BASE_URL}/contracts/${contractId}/files/${fileId}/`,
      { responseType: 'blob' },
    );
    return response.data;
  },

  // --- AI extraction (Phase 4) --------------------------------------------

  getFileAnalysis: async (contractId, fileId) => {
    const response = await axiosInstance.get(
      `${BASE_URL}/contracts/${contractId}/files/${fileId}/analysis/`,
    );
    return response.data;
  },

  retryFileAnalysis: async (contractId, fileId) => {
    const response = await axiosInstance.post(
      `${BASE_URL}/contracts/${contractId}/files/${fileId}/analysis/retry/`,
    );
    return response.data;
  },

  // --- Edit + resend (Phase 6) --------------------------------------------

  // PATCH the contract. The backend only honours the keys listed in
  // ``LegalContractUpdateSerializer.Meta.fields``; everything else
  // (C-number, FKs, audit) is silently ignored. Returns the full
  // detail payload — callers should replace local state in place.
  update: async (id, payload) => {
    const response = await axiosInstance.patch(
      `${BASE_URL}/contracts/${id}/`,
      payload,
    );
    return response.data;
  },

  // Re-fire the HAMSTIR counterparty email for the pair this contract
  // belongs to. Returns ``{ sent, recipient, sale_contract_number,
  // purchase_contract_number }`` so the caller can flash a useful
  // message.
  resendEmail: async (id) => {
    const response = await axiosInstance.post(
      `${BASE_URL}/contracts/${id}/resend-email/`,
    );
    return response.data;
  },

  // --- Solids recap contracts, status, Legal review ------------------------
  // Each returns the contract detail payload plus ``summary`` (the recap
  // screen's shape), so the contract page and the recap's Contract block
  // both update from one response.

  // Create the contract of a saved solids recap leg. ``mode`` is 'generate'
  // (the standard contract) or 'upload' (``file`` is the external contract).
  createFromRecap: async (tradeRecapId, mode, file) => {
    const form = new FormData();
    form.append('trade_recap_id', tradeRecapId);
    form.append('mode', mode);
    if (file) form.append('file', file);
    const response = await axiosInstance.post(
      `${BASE_URL}/contracts/from-recap/`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  // 'in_progress' | 'final' | 'signed'
  setStatus: async (id, status) => {
    const response = await axiosInstance.patch(
      `${BASE_URL}/contracts/${id}/status/`,
      { status },
    );
    return response.data;
  },

  sendToLegal: async (id) => {
    const response = await axiosInstance.post(`${BASE_URL}/contracts/${id}/send-to-legal/`);
    return response.data;
  },

  markReviewed: async (id, note = '') => {
    const response = await axiosInstance.post(
      `${BASE_URL}/contracts/${id}/mark-reviewed/`,
      { note },
    );
    return response.data;
  },

  // --- Add contract in Legal (upload), expiry and reminders --------------

  // The file chosen in the Add contract window. The server holds it as a
  // draft and reads its expiry date in the background: poll getDraft(id).
  uploadDraft: async (file, onUploadProgress) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post(
      `${BASE_URL}/contracts/drafts/`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress },
    );
    return response.data;
  },

  getDraft: async (draftId) => {
    const response = await axiosInstance.get(`${BASE_URL}/contracts/drafts/${draftId}/`);
    return response.data;
  },

  // { draft_id, entity, counterparty | counterparty_text, status,
  //   expiry_date | no_expiry } -> the new contract's detail payload.
  addContract: async (payload) => {
    const response = await axiosInstance.post(`${BASE_URL}/contracts/add/`, payload);
    return response.data;
  },

  // { expiry_date, no_expiry }
  setExpiry: async (id, payload) => {
    const response = await axiosInstance.patch(`${BASE_URL}/contracts/${id}/expiry/`, payload);
    return response.data;
  },

  // Your own reminder: { remind_on: 'YYYY-MM-DD', note }
  addReminder: async (id, payload) => {
    const response = await axiosInstance.post(`${BASE_URL}/contracts/${id}/reminders/`, payload);
    return response.data;
  },

  removeReminder: async (id, reminderId) => {
    const response = await axiosInstance.delete(`${BASE_URL}/contracts/${id}/reminders/${reminderId}/`);
    return response.data;
  },
};

// The server's own refusal sentence, else ``fallback``.
export const legalRefusal = (err, fallback) => (
  err?.response?.data?.detail || fallback
);

// Contract status choices, in the order the selector shows them.
export const CONTRACT_STATUS_OPTIONS = [
  { value: 'in_progress', label: 'In progress' },
  { value: 'final', label: 'Final' },
  { value: 'signed', label: 'Signed' },
];

export default legalContractService;
